import apiService from 'services/api';
import apiRoutes from 'services/api/routes';
import { getAccessToken } from 'lib/auth';
import { ECurrencyByWeightType, IMarker } from 'store/formula-entry/types';
import { EVolumeUnit } from 'data/volume-units';
import { FormulasInterface } from 'store/deals/types';
import { IDealProduct } from 'store/field-options/types';
import { ICostFormula, ICostFormulaGroup } from 'store/cost-formulas/types';
import { AxiosResponse } from 'axios';
import { IFormulas, ImfgMarkers, TConcatArraysReturnType } from './types';

/**
 * Takes a string formula and returns a Promise which resolves a
 * error object if valid, or an error object containing a message
 * field if there is an issue with the formula
 *
 * @param formula Formula to validate
 */

export function validateFormula(
  dealFormulas: FormulasInterface[] | undefined,
  formulas?: IFormulas[]
): Promise<AxiosResponse> {
  return apiService.post(
    apiRoutes.formulaValidation,
    { formulas: formulas?.map(({ formula, name }) => ({ formula, name })), dealFormulas },
    {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    }
  );
}

// Generates the appropriate input label based on product volume unit
export function getUnitLabel(products: IDealProduct[] | undefined, productIndex: number | null): string {
  if (!products || productIndex === null) {
    return '';
  }
  const unit = products[productIndex].volumeUnit;
  const displayUnit =
    unit === EVolumeUnit.ton || unit === EVolumeUnit.kton
      ? ECurrencyByWeightType.DOLLARS_PER_TON
      : ECurrencyByWeightType.CENTS_PER_POUND;
  return `(${displayUnit})`;
}

const timeoutInterval = 500;

// Accepts a React hook setter callback in order to create
// a timeout function which is set to clear itself after
// 500ms in order to prevent double-click actions from
// inserting duplicate markers into the formula
export function triggerDuplicateMarkerPreventionTimer(callback: Function) {
  const t = setTimeout(() => {
    callback(null);
  }, timeoutInterval);
  callback(t);
}

export function concatArrays({
  formulas,
  availableMarkers,
  mfgMarkers,
}: {
  formulas?: FormulasInterface[];
  availableMarkers: IMarker[] | null;
  mfgMarkers: ImfgMarkers[];
}) {
  let markersList: TConcatArraysReturnType[] = [];
  if (formulas && availableMarkers) {
    markersList = [...formulas, ...availableMarkers, ...mfgMarkers];
  }
  return markersList;
}

export function getMfgMarkers(arr: ICostFormulaGroup[]) {
  return arr
    ?.reduce(
      (accumulator: ICostFormula[], currentValue: { formulas: ICostFormula[] }) =>
        accumulator.concat(currentValue.formulas),
      []
    )
    .map((fmg: ICostFormula) => ({ ...fmg, types: 'variableMfgCost' }));
}
